import { usePostHog } from 'posthog-js/react';
import { type ReactElement, useEffect, useState } from 'react';

import {
    automationBaseAnalyticsProperties,
    captureAutomationEvent,
} from '@apps/web/components/automations/automation_analytics';
import { formatErrorMessage } from '@apps/web/components/automations/automation_api';
import { coordinatorAutomationProperties } from '@apps/web/components/automations/customer/automation_analytics';
import {
    automationCreateBody,
    type AutomationDialogForm,
    automationFormFromRead,
    automationUpdateBody,
    canSaveAutomationForm,
    EMPTY_AUTOMATION_FORM,
} from '@apps/web/components/automations/customer/automation_dialog_form';
import { WEEKDAY_LABELS } from '@apps/web/components/automations/customer/automation_schedule_summary';
import { type CoordinatorAutomationRead } from '@apps/web/components/automations/types';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Textarea } from '@apps/web/components/ui/textarea';
import { toast } from '@apps/web/lib/app_notifications';
import { EVENTS } from '@apps/web/lib/events';
import {
    useCoordinatorAutomationActions,
    useOrganization,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const INSTRUCTIONS_PLACEHOLDER =
    'Review every shift starting in the next 7 days and tell me which ones still have no caregiver.';

const TRIGGER_OPTIONS = [
    { value: 'schedule', label: 'On a schedule' },
] as const;

const REPEAT_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
] as const;

export function AutomationDialog({
    automation,
    open,
    onOpenChange,
    organizationId,
    onSaved,
}: {
    readonly automation: CoordinatorAutomationRead | null;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationId: string;
    readonly onSaved: () => Promise<void>;
}): ReactElement {
    const posthog = usePostHog();
    const { createAutomation, updateAutomation } =
        useCoordinatorAutomationActions();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'coordinator-automation-dialog',
    });
    const [form, setForm] = useState<AutomationDialogForm>(
        EMPTY_AUTOMATION_FORM,
    );
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        const next =
            automation === null
                ? EMPTY_AUTOMATION_FORM
                : automationFormFromRead(automation);
        setForm(next);
        setError(null);
    }, [automation, open]);

    function updateForm(changes: Partial<AutomationDialogForm>): void {
        setForm((current) => ({ ...current, ...changes }));
    }

    const timezone = organization?.timezone ?? automation?.timezone ?? null;

    async function save(): Promise<void> {
        setIsSaving(true);
        setError(null);
        try {
            if (automation === null) {
                await createAutomation(
                    organizationId,
                    automationCreateBody(form),
                );
                captureAutomationEvent(posthog, EVENTS.AUTOMATION_CREATED, {
                    ...automationBaseAnalyticsProperties({ organizationId }),
                    trigger_type: form.triggerType,
                    schedule_kind: form.kind,
                });
                toast.success('Routine created', {
                    persistNotification: false,
                });
            } else {
                await updateAutomation(
                    organizationId,
                    automation.id,
                    automationUpdateBody(form),
                );
                captureAutomationEvent(
                    posthog,
                    EVENTS.AUTOMATION_EDITED,
                    coordinatorAutomationProperties({
                        automation,
                        organizationId,
                    }),
                );
                toast.success('Changes saved', { persistNotification: false });
            }
        } catch (caught) {
            setError(formatErrorMessage(caught, 'Couldn’t save this routine.'));
            return;
        } finally {
            setIsSaving(false);
        }
        // The write already landed, so the dialog closes before the list is
        // re-read; a failed re-read must not invite a duplicate save.
        onOpenChange(false);
        try {
            await onSaved();
        } catch {
            toast.error('Couldn’t refresh the list.', {
                persistNotification: false,
            });
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[540px]">
                <DialogHeader>
                    <DialogTitle>
                        {automation === null ? 'New routine' : 'Edit routine'}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="automation-name">Name</Label>
                        <Input
                            id="automation-name"
                            value={form.name}
                            placeholder="Weekly open-shift risk review"
                            onChange={(event) =>
                                updateForm({ name: event.target.value })
                            }
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="automation-instructions">
                            Instructions
                        </Label>
                        <Textarea
                            id="automation-instructions"
                            rows={6}
                            value={form.prompt}
                            placeholder={INSTRUCTIONS_PLACEHOLDER}
                            onChange={(event) =>
                                updateForm({ prompt: event.target.value })
                            }
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>When it runs</Label>
                        <SegmentedControl
                            aria-label="When it runs"
                            options={TRIGGER_OPTIONS}
                            value={form.triggerType}
                            onValueChange={(triggerType) =>
                                updateForm({ triggerType })
                            }
                        />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="automation-repeat">Repeat</Label>
                            <Select
                                value={form.kind}
                                onValueChange={(kind) =>
                                    updateForm({
                                        kind:
                                            kind === 'daily'
                                                ? 'daily'
                                                : 'weekly',
                                    })
                                }
                            >
                                <SelectTrigger
                                    id="automation-repeat"
                                    className="w-full"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {REPEAT_OPTIONS.map((option) => (
                                        <SelectItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {form.kind === 'weekly' ? (
                            <div className="space-y-1.5">
                                <Label htmlFor="automation-day">Day</Label>
                                <Select
                                    value={String(form.weekday)}
                                    onValueChange={(weekday) =>
                                        updateForm({
                                            weekday: Number(weekday),
                                        })
                                    }
                                >
                                    <SelectTrigger
                                        id="automation-day"
                                        className="w-full"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {WEEKDAY_LABELS.map((label, index) => (
                                            <SelectItem
                                                key={label}
                                                value={String(index)}
                                            >
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : null}
                        <div className="space-y-1.5">
                            <Label htmlFor="automation-time">Time</Label>
                            <Input
                                id="automation-time"
                                type="time"
                                value={form.time}
                                onChange={(event) =>
                                    updateForm({ time: event.target.value })
                                }
                            />
                            {timezone === null ? null : (
                                <p className="text-muted-foreground text-xs">
                                    {timezone}
                                </p>
                            )}
                        </div>
                    </div>
                    {error === null ? null : (
                        <p role="alert" className="text-destructive text-sm">
                            {error}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={isSaving || !canSaveAutomationForm(form)}
                        onClick={() => void save()}
                    >
                        {automation === null
                            ? 'Create routine'
                            : 'Save changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
